import { useCallback, useEffect, useRef } from 'react';
import type { Nullable } from 'corgi-types';

import { GrowthbookFlag } from 'common/constants/feature-flags';
import { CommonBannerEvent, MediaType, VideoEvent } from 'common/enums/Tracking';
import { usePromoEvents } from 'common/hooks/dynamic-yield';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import { useIsObserved } from 'common/hooks/intersection-observer';
import type { CampaignTrackingData } from 'common/tracking/campaignEventData';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import type { Element } from 'common/types/tracking';
import type { PrismicVideo } from 'common/types/video';

interface UsePromoTrackingParams {
  tracking: CampaignTrackingData;
  video: Nullable<PrismicVideo>;
  title?: Nullable<string>;
}

const getVideoEventData = ({
  videoEvent,
  label,
  type,
}: {
  videoEvent: VideoEvent;
  label?: Nullable<string>;
  type: string;
}) => ({
  event: videoEvent,
  type,
  ...(label && { label }),
});

const useTracking = ({ tracking, video, title }: UsePromoTrackingParams) => {
  const hasTrackedViewEvent = useRef(false);
  const [ref, isObserved] = useIsObserved();
  const videoFeatureEnabled = useFeatureFlagIsOn(GrowthbookFlag.EnableVideoContent);

  const isVideoVariant = Boolean(videoFeatureEnabled && video);
  const mediaElement = isVideoVariant ? MediaType.Video : MediaType.Image;

  const { dyDecisionId, dyVariationId, placement, name } = tracking;
  const hasDyData = Boolean(dyDecisionId && dyVariationId);

  const { onPromoView, onPromoClick: onPromoClickEvent } = usePromoEvents({
    dyDecisionId,
    dyVariationId,
    placement,
    name,
    mediaElement,
    ...(isVideoVariant && title && { label: title }),
  });

  const onVideoStart = useCallback(() => {
    dispatchTrackingEventGA4(
      getVideoEventData({
        videoEvent: VideoEvent.VideoStart,
        type: MediaType.Video,
        label: title,
      })
    );
  }, [title]);

  const onVideoComplete = useCallback(() => {
    dispatchTrackingEventGA4(
      getVideoEventData({
        videoEvent: VideoEvent.VideoComplete,
        type: MediaType.Video,
        label: title,
      })
    );
  }, [title]);

  const onPromoClick = useCallback(
    (element: Element | MediaType) => {
      if (hasDyData) {
        onPromoClickEvent({ element });
      } else {
        dispatchTrackingEventGA4({
          event: CommonBannerEvent.Click,
          ...(title && { label: title }),
          element,
          type: mediaElement,
        });
      }
    },
    [hasDyData, onPromoClickEvent, title, mediaElement]
  );

  useEffect(() => {
    if (hasTrackedViewEvent.current) return;
    if (!isObserved) return;

    if (hasDyData) {
      onPromoView();
    } else {
      dispatchTrackingEventGA4({
        event: CommonBannerEvent.View,
        type: mediaElement,
        ...(title && { label: title }),
      });
    }
    hasTrackedViewEvent.current = true;
  }, [isObserved, hasDyData, onPromoView, mediaElement, title]);

  return {
    ref,
    isVideoVariant,
    mediaElement,
    onPromoClick,
    onVideoStart,
    onVideoComplete,
  };
};

export default useTracking;
